import { normalizeFilename } from '@/api/prompts'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select, type SelectOption } from '@/components/Select'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import type { ExtractorEntry } from '@/types/bundle'

export interface ExtractorFormProps {
  id: string
  extractor: ExtractorEntry
  onChange: (extractor: ExtractorEntry) => void
  promptFileOptions: SelectOption[]
}

// The expanded accordion body - mirrors LookupEntryForm's role, rendered via
// DataTable's `renderExpanded` directly below the collapsed row.
export function ExtractorForm({ id, extractor, onChange, promptFileOptions }: ExtractorFormProps) {
  const imageDpi = extractor.image?.dpi
  const imageFormat = extractor.image?.format
  const imageMaxPages = extractor.image?.max_pages

  function updateImage(patch: Partial<NonNullable<ExtractorEntry['image']>>) {
    onChange({ ...extractor, image: { ...extractor.image, ...patch } })
  }
  return (
    <div className="flex flex-col gap-4 bg-surface p-4">
      <label className="flex w-fit items-center gap-2 text-body text-text-primary">
        <input
          type="checkbox"
          checked={extractor.enabled}
          onChange={(event) => onChange({ ...extractor, enabled: event.target.checked })}
        />
        Enabled
        <InfoTooltip text="Whether this extractor can be selected by a product type's Extraction section. Disabling it hides it from that picker." />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-model-id-${id}`}>
            Model ID
            <InfoTooltip text="The exact Bedrock model or inference-profile identifier this extractor calls (e.g. eu.anthropic.claude-sonnet-4-6)." />
          </label>
          <input
            id={`extractor-model-id-${id}`}
            type="text"
            value={extractor.model_id}
            onChange={(event) => onChange({ ...extractor, model_id: event.target.value })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
          {extractor.model_id.trim().length === 0 && (
            <span className="text-meta text-danger">Model ID is required</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Type
            <InfoTooltip text="Identifies the extractor engine implementation used (currently only one exists: Bedrock Vision). This does not limit you to one extractor - add as many extractor entries as you like below, each with its own Model ID, to use different models." />
          </span>
          <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
            {extractor.type}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Region
            <InfoTooltip text="Fixed by the platform - eu-west-2 is currently the only supported region." />
          </span>
          <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
            {extractor.region}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-tier-${id}`}>
            Tier <span className="text-text-disabled">(optional)</span>
            <InfoTooltip text="A cost/capability label (t0-t4) for your own reference - informational only, not enforced or read by any code." />
          </label>
          <input
            id={`extractor-tier-${id}`}
            type="text"
            value={extractor.tier ?? ''}
            onChange={(event) => onChange({ ...extractor, tier: event.target.value || undefined })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-max-tokens-${id}`}>
            Max tokens <span className="text-text-disabled">(optional)</span>
            <InfoTooltip text="Caps the length of the model's response. Falls back to the model's own default when left blank." />
          </label>
          <input
            id={`extractor-max-tokens-${id}`}
            type="number"
            value={numberField(extractor.max_tokens)}
            onChange={(event) => onChange({ ...extractor, max_tokens: parseOptionalNumber(event.target.value) })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-temperature-${id}`}>
            Temperature <span className="text-text-disabled">(optional)</span>
            <InfoTooltip text="Controls randomness in the model's output - 0 is fully deterministic, higher values increase variety. Some Bedrock models reject a request that sets both Temperature and Top P together." />
          </label>
          <input
            id={`extractor-temperature-${id}`}
            type="number"
            step="0.1"
            value={numberField(extractor.temperature)}
            onChange={(event) => onChange({ ...extractor, temperature: parseOptionalNumber(event.target.value) })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-top-p-${id}`}>
            Top P <span className="text-text-disabled">(optional, 0-1)</span>
            <InfoTooltip text="An alternate sampling method to Temperature - probability mass to sample from. Some Bedrock models reject a request that sets both Temperature and Top P together - for the current model this has been confirmed live, so leave this blank." />
          </label>
          <input
            id={`extractor-top-p-${id}`}
            type="number"
            step="0.1"
            min={0}
            max={1}
            value={numberField(extractor.top_p)}
            onChange={(event) => onChange({ ...extractor, top_p: parseOptionalNumber(event.target.value) })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-timeout-${id}`}>
            Timeout seconds <span className="text-text-disabled">(optional)</span>
            <InfoTooltip text="The maximum time to wait for a single Bedrock call to respond before giving up - may be shortened automatically for a call made close to the platform's own processing-time limit." />
          </label>
          <input
            id={`extractor-timeout-${id}`}
            type="number"
            value={numberField(extractor.timeout_seconds)}
            onChange={(event) => onChange({ ...extractor, timeout_seconds: parseOptionalNumber(event.target.value) })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-safety-margin-${id}`}>
            Safety margin seconds <span className="text-text-disabled">(optional)</span>
            <InfoTooltip text="A buffer kept in reserve before the platform's own hard processing-time limit. Checked before every Bedrock call and retry - if too little time remains, the request is stopped cleanly and the caller gets EXT_010_EXTRACTION_TIMEOUT, rather than risking a call that gets killed mid-request with no response at all." />
          </label>
          <input
            id={`extractor-safety-margin-${id}`}
            type="number"
            value={numberField(extractor.safety_margin_seconds)}
            onChange={(event) => onChange({ ...extractor, safety_margin_seconds: parseOptionalNumber(event.target.value) })}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-prompt-file-${id}`}>
          Prompt template file
          <InfoTooltip text="The default prompt file this extractor uses, chosen from the bundle's prompts/ folder. Edit its actual text content from the Prompts screen - this only sets which file it points to." />
        </label>
        <Select
          id={`extractor-prompt-file-${id}`}
          value={extractor.prompt_template_file ? normalizeFilename(extractor.prompt_template_file) : ''}
          onChange={(filename) => onChange({ ...extractor, prompt_template_file: `prompts/${filename}` })}
          options={promptFileOptions}
          className="max-w-md"
        />
      </div>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface-raised p-4">
        <h3 className="flex items-center gap-1 text-meta font-semibold text-text-primary">
          Image
          <InfoTooltip text="Page-to-image rendering settings the Extraction Engine uses before sending pages to the vision model (app/core/pdf_render.py)." />
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-image-dpi-${id}`}>
              DPI <span className="text-text-disabled">(optional)</span>
              <InfoTooltip text="Resolution (dots per inch) each PDF page is rendered at before being sent to the vision model. Higher values improve legibility of small text but increase image size/cost. Defaults to 150 when left blank." />
            </label>
            <input
              id={`extractor-image-dpi-${id}`}
              type="number"
              value={numberField(imageDpi)}
              onChange={(event) => updateImage({ dpi: parseOptionalNumber(event.target.value) })}
              className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-image-format-${id}`}>
              Format <span className="text-text-disabled">(optional)</span>
              <InfoTooltip text="Image format for rendered pages. Currently the extraction engine always renders PNG regardless of this value - kept here for forward compatibility, not yet enforced by any code." />
            </label>
            <input
              id={`extractor-image-format-${id}`}
              type="text"
              value={imageFormat ?? ''}
              onChange={(event) => updateImage({ format: event.target.value || undefined })}
              placeholder="PNG"
              className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor={`extractor-image-max-pages-${id}`}>
              Max pages <span className="text-text-disabled">(optional)</span>
              <InfoTooltip text="Maximum number of page images sent to the model in a single Bedrock call - a document with more pages is split into several calls (chunks) of about equal size, sent at the same time (Global Configs > Chunking), not truncated. Also capped by a platform-wide safety limit. Defaults to 10 when left blank." />
            </label>
            <input
              id={`extractor-image-max-pages-${id}`}
              type="number"
              min={1}
              value={numberField(imageMaxPages)}
              onChange={(event) => updateImage({ max_pages: parseOptionalNumber(event.target.value) })}
              className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
            />
          </div>
        </div>
      </section>
    </div>
  )
}
